"use client";

import { useEffect, useRef, useState, FormEvent } from "react";
import { useRouter } from "next/navigation";
import SearchIcon from "../icons/SearchIcon";
import CloseIcon from "../icons/CloseIcon";
import { Button } from "@/components";

interface SearchBarProps {
  searchQuery?: string;
  selectedLocationId: number | null;
  stores: Array<{ covaLocationId: number }>;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
}

export default function SearchBar({
  searchQuery = "",
  selectedLocationId,
  stores,
  isExpanded,
  onExpand,
  onCollapse,
}: SearchBarProps) {
  const router = useRouter();
  const [searchInput, setSearchInput] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus only when expanding (avoid stealing focus on unrelated rerenders)
  useEffect(() => {
    if (!isExpanded) return;
    const t = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [isExpanded]);

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    const locationId =
      selectedLocationId || (stores.length > 0 ? stores[0].covaLocationId : null);
    if (locationId) {
      const params = new URLSearchParams();
      if (searchInput.trim()) {
        params.set("q", searchInput.trim());
      }
      router.push(`/search?${params.toString()}`);
      onCollapse();
    }
  };

  if (!isExpanded) {
    return (
      <Button
        type="button"
        onClick={onExpand}
        variant="secondary"
        className="h-auto p-2"
        aria-label="Search"
      >
        <SearchIcon size={20} className="w-5 h-5" />
      </Button>
    );
  }

  return (
    <form
      onSubmit={handleSearchSubmit}
      className="flex items-center gap-2 flex-1 max-w-md"
    >
      <div className="relative flex-1">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search products..."
          className="w-full px-4 py-2 pl-10 rounded-lg border border-neutral-10 focus:outline-none focus:ring-2 focus:ring-blue-100"
          ref={inputRef}
        />
        <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary" />
      </div>
      <Button
        type="button"
        onClick={onCollapse}
        variant="secondary"
        className="h-auto p-2"
        aria-label="Close search"
      >
        <CloseIcon size={20} className="w-5 h-5" />
      </Button>
    </form>
  );
}

